import React, { useEffect, useState, useCallback } from "react";
import { ArrowLeft, Trash2, Plus, Upload, X, ChevronUp, ChevronDown, Eye, EyeOff, Pencil, BookOpen, PlayCircle, Sparkles, Youtube, Image, RefreshCw, Check, ExternalLink, Link2 } from "lucide-react";
import { toast } from "sonner";
import { uploadFile } from "./upload";
import { renderInline } from "./inline";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full rounded-lg border border-[#26201625] bg-white/80 px-3 py-2 text-sm text-[#262016] outline-none focus:border-[#B5793F]";
const labelCls = "block text-[11px] uppercase tracking-[0.14em] text-[#8A7E6B] font-medium mb-1.5";
const btnPrimary = { background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" };
const iconBtn = "w-8 h-8 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-[#262016] hover:border-[#262016] disabled:opacity-30";
const ytIdOf = (src) => (src?.match(/(?:v=|embed\/|youtu\.be\/|shorts\/)([\w-]{6,})/) || [])[1];
const resolve = (u) => (u?.startsWith("/api/") ? `${API}${u}` : u);

const api = async (method, path, body) => {
  const r = await fetch(`${API}/api/admin/lms/${path}`, {
    method,
    credentials: "include",
    ...(body ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {}),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Request failed");
  return r.json();
};

function UploadBtn({ onDone, accept, testid, label = "Upload" }) {
  const [prog, setProg] = useState(null);
  const handle = async (file) => {
    if (!file) return;
    setProg(0);
    try {
      const href = await uploadFile(file, setProg);
      onDone(href, file);
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setProg(null); }
  };
  return (
    <label className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#26201625] text-sm text-[#5C5042] cursor-pointer hover:border-[#262016]">
      <Upload size={14} /> {prog === null ? label : `${prog}%`}
      <input type="file" accept={accept} className="hidden" data-testid={testid}
             onChange={(e) => { handle(e.target.files?.[0]); e.target.value = ""; }} />
    </label>
  );
}

function NotesPreview({ raw }) {
  const blocks = [];
  for (const chunk of (raw || "").split(/\n\s*\n/)) {
    const t = chunk.trim();
    if (!t) continue;
    if (t.startsWith("## ")) blocks.push({ h: t.slice(3).trim() });
    else if (t.split("\n").every((l) => l.trim().startsWith("- "))) blocks.push({ ul: t.split("\n").map((l) => l.trim().slice(2)) });
    else blocks.push({ p: t });
  }
  if (!blocks.length) return <p className="text-xs text-[#8A7E6B] italic">Nothing to preview yet.</p>;
  return blocks.map((b, i) => {
    if (b.h) return <p key={i} className="rp-display text-base font-medium text-[#262016] mt-3 mb-1">{renderInline(b.h)}</p>;
    if (b.ul) return (
      <ul key={i} className="my-2 space-y-1">
        {b.ul.map((li, j) => (
          <li key={j} className="flex gap-2 text-[13px] text-[#5C5042]">
            <span className="shrink-0 mt-[7px] w-1 h-1 rounded-full" style={{ background: "#B5793F" }} />{renderInline(li)}
          </li>
        ))}
      </ul>
    );
    return <p key={i} className="text-[13px] leading-relaxed text-[#5C5042] my-2">{renderInline(b.p)}</p>;
  });
}

/* ---------- Section-based lesson editor ---------- */
const SECTION_TYPES = [
  { type: "text", label: "Text", icon: Pencil },
  { type: "video", label: "Video", icon: PlayCircle },
  { type: "image", label: "Image", icon: Image },
  { type: "link", label: "Link", icon: Link2 },
  { type: "file", label: "File", icon: Upload },
];

function genId() { return crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36); }

function buildSectionsFromLegacy(lesson) {
  if (lesson?.sections?.length) return lesson.sections;
  const sections = [];
  let order = 0;
  const notes = (lesson?.notes || "").trim();
  if (notes) { sections.push({ id: genId(), type: "text", content: notes, order: order++ }); }
  const vids = lesson?.videos || [];
  const legacy = (lesson?.video || "").trim();
  if (legacy && !vids.includes(legacy)) vids.unshift(legacy);
  for (const v of vids) { if (v?.trim()) sections.push({ id: genId(), type: "video", url: v.trim(), order: order++ }); }
  for (const img of (lesson?.images || [])) {
    const u = typeof img === "string" ? img : img?.url || "";
    if (u) sections.push({ id: genId(), type: "image", url: u, caption: "", order: order++ });
  }
  for (const f of (lesson?.files || [])) {
    if (f) sections.push({ id: genId(), type: "file", url: f.href || f.url || "", filename: f.label || f.name || "File", order: order++ });
  }
  return sections;
}

function InsertButton({ onInsert }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex justify-center py-1.5">
      <button onClick={() => setOpen(!open)} data-testid="section-insert-btn"
              className="inline-flex items-center gap-1 px-3 py-1 text-[11px] font-medium text-[#8A7E6B] hover:text-[#B5793F] border border-dashed border-[#26201618] hover:border-[#B5793F] rounded-full transition-colors">
        <Plus size={10} /> Insert section
      </button>
      {open && (
        <div className="absolute top-full mt-1 z-10 bg-white rounded-xl border border-[#26201620] shadow-lg p-1.5 flex gap-1">
          {SECTION_TYPES.map(({ type, label, icon: Ic }) => (
            <button key={type} onClick={() => { onInsert(type); setOpen(false); }}
                    data-testid={`section-insert-${type}`}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-[#5C5042] hover:bg-[#B5793F12] hover:text-[#B5793F] transition-colors">
              <Ic size={12} /> {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TextSection({ section, onChange, courseTitle, lessonTitle }) {
  const [preview, setPreview] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editInstr, setEditInstr] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  const aiWrite = async () => {
    if (!lessonTitle?.trim()) return toast.error("Give the lesson a title first");
    setAiBusy(true);
    try {
      const d = await api("POST", "ai/lesson", { module_title: courseTitle, lesson_title: lessonTitle });
      onChange({ ...section, content: d.notes });
      setPreview(true);
      toast.success("Notes drafted");
    } catch (e) { toast.error(e.message); }
    finally { setAiBusy(false); }
  };

  const aiEdit = async () => {
    if (!editInstr.trim()) return toast.error("Describe how to change the text");
    if (!section.content?.trim()) return toast.error("Write or generate notes first");
    setEditBusy(true);
    try {
      const d = await api("POST", "ai/lesson-edit", {
        notes: section.content, instructions: editInstr.trim(),
        module_title: courseTitle, lesson_title: lessonTitle,
      });
      onChange({ ...section, content: d.notes });
      setEditInstr(""); setEditMode(false); setPreview(true);
      toast.success("Section updated");
    } catch (e) { toast.error(e.message); }
    finally { setEditBusy(false); }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={aiWrite} disabled={aiBusy || editBusy} data-testid="section-ai-write"
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#B5793F] border border-[#B5793F55] rounded-full px-3 py-1 hover:bg-[#B5793F12] disabled:opacity-50">
          <Sparkles size={10} /> {aiBusy ? "Writing…" : "Write with AI"}
        </button>
        <button onClick={() => setEditMode(!editMode)} disabled={aiBusy} data-testid="section-ai-edit-toggle"
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#B5793F] border border-[#B5793F55] rounded-full px-3 py-1 hover:bg-[#B5793F12] disabled:opacity-50">
          <Pencil size={9} /> {editMode ? "Cancel" : "Edit with AI"}
        </button>
        <button onClick={() => setPreview(!preview)} className="text-[11px] font-semibold text-[#B5793F] underline underline-offset-2">
          {preview ? "Hide preview" : "Preview"}
        </button>
      </div>
      {editMode && (
        <div className="flex gap-2">
          <input value={editInstr} onChange={(e) => setEditInstr(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && aiEdit()}
                 placeholder="e.g. Make it more concise · Add examples" className={inputCls} data-testid="section-ai-edit-input" autoFocus />
          <button onClick={aiEdit} disabled={editBusy} data-testid="section-ai-edit-go"
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg text-[12px] font-semibold text-[#F6F1E5] disabled:opacity-50" style={btnPrimary}>
            <Sparkles size={10} /> {editBusy ? "…" : "Apply"}
          </button>
        </div>
      )}
      <div className={preview ? "grid grid-cols-1 md:grid-cols-2 gap-3" : ""}>
        <textarea value={section.content || ""} onChange={(e) => onChange({ ...section, content: e.target.value })} rows={6}
                  placeholder={"## Heading\n\nContent paragraph…\n\n- Bullet point"} className={inputCls} data-testid="section-text-content" />
        {preview && (
          <div className="rounded-lg border border-[#26201618] bg-white/60 p-3 overflow-y-auto text-sm" style={{ maxHeight: 200 }}>
            <NotesPreview raw={section.content} />
          </div>
        )}
      </div>
    </div>
  );
}

function VideoSection({ section, onChange, lessonTitle }) {
  const [ytSearching, setYtSearching] = useState(false);
  const [ytResults, setYtResults] = useState(null);
  const yt = ytIdOf(section.url);

  const searchYoutube = async () => {
    if (!lessonTitle?.trim()) return toast.error("Give the lesson a title first");
    setYtSearching(true);
    try {
      const r = await fetch(`${API}/api/admin/lms/youtube/search?q=${encodeURIComponent(lessonTitle)}&max_results=4`, { credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Search failed");
      setYtResults(d.results || []);
      if ((d.results || []).length === 0) toast.info("No videos found from approved channels");
    } catch (e) { toast.error(e.message); }
    finally { setYtSearching(false); }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-center">
        <input value={section.url || ""} onChange={(e) => onChange({ ...section, url: e.target.value })}
               placeholder="https://youtube.com/watch?v=…" className={inputCls} data-testid="section-video-url" />
        <UploadBtn accept="video/*" testid="section-video-upload" label="Upload"
                   onDone={(href) => onChange({ ...section, url: href })} />
        <button onClick={searchYoutube} disabled={ytSearching} data-testid="section-yt-search"
                className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-[#B5793F] border border-[#B5793F55] rounded-full px-3 py-1 hover:bg-[#B5793F12] disabled:opacity-50">
          <Youtube size={11} /> {ytSearching ? "…" : "Find"}
        </button>
      </div>
      {ytResults?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ytResults.map((v) => (
            <button key={v.video_id} onClick={() => { onChange({ ...section, url: v.url }); setYtResults(null); toast.success("Video selected"); }}
                    className="flex gap-2 p-2 rounded-lg border border-[#26201618] bg-white/70 hover:border-[#B5793F] text-left text-xs"
                    data-testid={`yt-result-${v.video_id}`}>
              {v.thumbnail && <img src={v.thumbnail} alt="" className="w-20 h-14 rounded object-cover shrink-0" />}
              <div className="min-w-0"><p className="font-medium text-[#262016] line-clamp-2">{v.title}</p><p className="text-[#8A7E6B] mt-0.5">{v.channel}</p></div>
            </button>
          ))}
          <button onClick={() => setYtResults(null)} className="text-[11px] text-[#8A7E6B] underline self-start">Dismiss</button>
        </div>
      )}
      {yt && (
        <div className="w-full max-w-xs rounded-lg overflow-hidden bg-black" style={{ aspectRatio: "16/9" }}>
          <iframe src={`https://www.youtube.com/embed/${yt}`} title="Preview" className="w-full h-full" frameBorder="0" allowFullScreen />
        </div>
      )}
      {section.url && !yt && section.url.startsWith("/api/") && (
        <div className="w-full max-w-xs rounded-lg overflow-hidden bg-black" style={{ aspectRatio: "16/9" }}>
          <video src={resolve(section.url)} controls controlsList="nodownload" preload="metadata" className="w-full h-full" />
        </div>
      )}
    </div>
  );
}

function ImageSection({ section, onChange, courseTitle, lessonTitle }) {
  const [genning, setGenning] = useState(false);
  const generateInfographic = async () => {
    if (!lessonTitle?.trim()) return toast.error("Give the lesson a title first");
    setGenning(true);
    try {
      const r = await fetch(`${API}/api/admin/lms/ai/infographic`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ topic: lessonTitle, description: `Module: ${courseTitle}. Lesson: ${lessonTitle}.` }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      onChange({ ...section, url: d.url, caption: "Infographic" });
      toast.success("Infographic generated");
    } catch (e) { toast.error(e.message); }
    finally { setGenning(false); }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {section.url && (
          <img src={resolve(section.url)} alt="" className="w-20 h-20 object-cover rounded-lg border border-[#26201620]" />
        )}
        <UploadBtn accept="image/*" testid="section-image-upload" label="Upload image"
                   onDone={(href) => onChange({ ...section, url: href })} />
        <button onClick={generateInfographic} disabled={genning} data-testid="section-gen-infographic"
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#B5793F] border border-[#B5793F55] rounded-full px-3 py-1 hover:bg-[#B5793F12] disabled:opacity-50">
          <Image size={11} /> {genning ? "Generating…" : "Generate infographic"}
        </button>
      </div>
      <input value={section.caption || ""} onChange={(e) => onChange({ ...section, caption: e.target.value })}
             placeholder="Caption (optional)" className={inputCls} data-testid="section-image-caption" />
    </div>
  );
}

function FileSection({ section, onChange }) {
  return (
    <div className="flex gap-2 items-center">
      <input value={section.filename || ""} onChange={(e) => onChange({ ...section, filename: e.target.value })}
             placeholder="Displayed filename" className={inputCls} data-testid="section-file-name" />
      <UploadBtn testid="section-file-upload" label={section.url ? "Replace" : "Upload"}
                 onDone={(href, file) => onChange({ ...section, url: href, filename: section.filename || file.name })} />
      {section.url && <span className="text-[11px] text-[#B5793F]"><Check size={12} className="inline" /> uploaded</span>}
    </div>
  );
}

function LinkSection({ section, onChange }) {
  return (
    <div className="space-y-2">
      <input value={section.url || ""} onChange={(e) => onChange({ ...section, url: e.target.value })}
             placeholder="https://example.com/article" className={inputCls} data-testid="section-link-url" />
      <input value={section.title || ""} onChange={(e) => onChange({ ...section, title: e.target.value })}
             placeholder="Link title — e.g. Official Canva Design Guide" className={inputCls} data-testid="section-link-title" />
      <input value={section.description || ""} onChange={(e) => onChange({ ...section, description: e.target.value })}
             placeholder="Short description (optional)" className={inputCls} data-testid="section-link-desc" />
      {section.url && (
        <a href={section.url} target="_blank" rel="noreferrer noopener"
           className="inline-flex items-center gap-1.5 text-[11px] text-[#B5793F] hover:underline">
          <ExternalLink size={10} /> Preview link
        </a>
      )}
    </div>
  );
}

const SECTION_LABELS = { text: "Text", video: "Video", image: "Image", file: "File", link: "Link" };
const SECTION_ICONS = { text: Pencil, video: PlayCircle, image: Image, file: Upload, link: Link2 };

function LessonPreview({ lesson, onEdit, onClose }) {
  const sections = lesson?.sections || [];
  const hasContent = sections.length > 0 || (lesson?.notes || "").trim();

  if (!hasContent) {
    return (
      <div className="rp-foam bg-white/70 p-8 text-center" data-testid="lesson-preview-empty">
        <BookOpen className="mx-auto mb-3 text-[#8A7E6B]" size={22} />
        <p className="text-sm text-[#8A7E6B] mb-4">This lesson has no content yet.</p>
        <button onClick={onEdit} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5]" style={btnPrimary}>
          <Pencil size={13} /> Add content
        </button>
      </div>
    );
  }

  return (
    <div className="rp-foam bg-white/70 p-5 md:p-6" data-testid="lesson-preview">
      <div className="flex items-center justify-between mb-5 pb-4 border-b border-[#26201615]">
        <div>
          <p className="rp-display text-xl font-light text-[#262016]">{lesson.title}</p>
          <p className="text-[11px] text-[#8A7E6B] mt-1">
            {sections.filter(s => s.type === "text").length} text
            {" · "}{sections.filter(s => s.type === "video").length} videos
            {" · "}{sections.filter(s => s.type === "link").length} links
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={onEdit} data-testid="preview-edit-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold border border-[#26201625] text-[#262016] hover:border-[#B5793F]">
            <Pencil size={13} /> Edit
          </button>
          <button onClick={onClose} className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-[13px] text-[#8A7E6B] hover:text-[#262016]">
            <X size={13} />
          </button>
        </div>
      </div>

      <div className="space-y-5">
        {sections.sort((a, b) => (a.order || 0) - (b.order || 0)).map((sec, idx) => {
          if (sec.type === "text") {
            return (
              <div key={sec.id || idx} className="prose prose-sm max-w-none text-[#262016]" data-testid={`preview-section-text-${idx}`}>
                {(sec.content || "").split("\n").map((line, i) => {
                  if (line.startsWith("# ")) return <h2 key={i} className="text-lg font-semibold text-[#262016] mt-4 mb-2">{line.slice(2)}</h2>;
                  if (line.startsWith("## ")) return <h3 key={i} className="text-base font-semibold text-[#262016] mt-3 mb-1.5">{line.slice(3)}</h3>;
                  if (line.startsWith("### ")) return <h4 key={i} className="text-sm font-semibold text-[#262016] mt-2 mb-1">{line.slice(4)}</h4>;
                  if (line.startsWith("- ") || line.startsWith("* ")) return <li key={i} className="ml-4 text-sm text-[#3B301F]">{line.slice(2)}</li>;
                  if (line.startsWith("**") && line.endsWith("**")) return <p key={i} className="text-sm font-semibold text-[#262016]">{line.slice(2, -2)}</p>;
                  if (!line.trim()) return <div key={i} className="h-2" />;
                  return <p key={i} className="text-sm text-[#3B301F] leading-relaxed">{line}</p>;
                })}
              </div>
            );
          }
          if (sec.type === "video") {
            const vid = ytIdOf(sec.url || sec.content);
            return (
              <div key={sec.id || idx} data-testid={`preview-section-video-${idx}`}>
                {vid ? (
                  <div className="relative w-full rounded-xl overflow-hidden border border-[#26201612]" style={{ paddingBottom: "56.25%" }}>
                    <iframe src={`https://www.youtube.com/embed/${vid}`} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope" allowFullScreen />
                  </div>
                ) : (
                  <a href={sec.url || sec.content} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#B5793F] hover:underline">
                    <PlayCircle size={14} /> {sec.url || sec.content}
                  </a>
                )}
              </div>
            );
          }
          if (sec.type === "link") {
            return (
              <a key={sec.id || idx} href={sec.url} target="_blank" rel="noopener noreferrer"
                 className="block rounded-xl border border-[#26201618] p-4 hover:border-[#B5793F55] transition-colors" data-testid={`preview-section-link-${idx}`}>
                <div className="flex items-start gap-3">
                  <ExternalLink size={14} className="shrink-0 mt-0.5 text-[#B5793F]" />
                  <div>
                    <p className="text-sm font-medium text-[#262016]">{sec.title || sec.url}</p>
                    {sec.description && <p className="text-xs text-[#8A7E6B] mt-1">{sec.description}</p>}
                    <p className="text-[11px] text-[#B5793F] mt-1 truncate">{sec.url}</p>
                  </div>
                </div>
              </a>
            );
          }
          if (sec.type === "image") {
            return (
              <div key={sec.id || idx} data-testid={`preview-section-image-${idx}`}>
                <img src={resolve(sec.url || sec.content)} alt={sec.caption || ""} className="max-w-full rounded-xl border border-[#26201612]" />
                {sec.caption && <p className="text-xs text-[#8A7E6B] mt-2 text-center">{sec.caption}</p>}
              </div>
            );
          }
          return null;
        })}
      </div>

      {/* Fallback: show legacy notes if no sections */}
      {sections.length === 0 && (lesson?.notes || "").trim() && (
        <div className="prose prose-sm max-w-none text-[#262016]">
          {lesson.notes.split("\n").map((line, i) => (
            <p key={i} className="text-sm text-[#3B301F] leading-relaxed">{line}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function LessonEditor({ courseId, courseTitle, submoduleId, lesson, nextOrder, onSaved, onCancel }) {
  const [title, setTitle] = useState(lesson?.title || "");
  const [sections, setSections] = useState(() => buildSectionsFromLegacy(lesson));
  const [busy, setBusy] = useState(false);
  const [wholeAiBusy, setWholeAiBusy] = useState(false);
  const [wholeEditMode, setWholeEditMode] = useState(false);
  const [wholeEditInstr, setWholeEditInstr] = useState("");
  const [wholeEditBusy, setWholeEditBusy] = useState(false);
  const [ytSearching, setYtSearching] = useState(false);
  const [ytResults, setYtResults] = useState(null);

  const updateSection = (idx, updated) => setSections((s) => s.map((sec, i) => (i === idx ? updated : sec)));
  const removeSection = (idx) => setSections((s) => s.filter((_, i) => i !== idx));
  const moveSection = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sections.length) return;
    setSections((s) => { const n = [...s]; [n[idx], n[j]] = [n[j], n[idx]]; return n.map((sec, i) => ({ ...sec, order: i })); });
  };
  const insertAt = (afterIdx, type) => {
    const newSec = { id: genId(), type, order: afterIdx + 1, content: "", url: "", caption: "", filename: "" };
    setSections((s) => { const n = [...s]; n.splice(afterIdx + 1, 0, newSec); return n.map((sec, i) => ({ ...sec, order: i })); });
  };
  const addSection = (type) => {
    setSections((s) => [...s, { id: genId(), type, order: s.length, content: "", url: "", caption: "", filename: "" }]);
  };

  // Whole-lesson AI write: generates text and replaces/adds a text section
  const wholeAiWrite = async () => {
    if (!title?.trim()) return toast.error("Give the lesson a title first");
    setWholeAiBusy(true);
    try {
      const d = await api("POST", "ai/lesson", { module_title: courseTitle, lesson_title: title });
      const existingTextIdx = sections.findIndex((s) => s.type === "text");
      if (existingTextIdx >= 0) {
        updateSection(existingTextIdx, { ...sections[existingTextIdx], content: d.notes });
      } else {
        setSections((s) => [{ id: genId(), type: "text", content: d.notes, order: 0 }, ...s.map((sec, i) => ({ ...sec, order: i + 1 }))]);
      }
      toast.success("AI wrote lesson content — review before saving");
    } catch (e) { toast.error(e.message); }
    finally { setWholeAiBusy(false); }
  };

  // Whole-lesson AI edit: combines all text sections, edits, puts back in first text section
  const wholeAiEdit = async () => {
    if (!wholeEditInstr.trim()) return toast.error("Describe how to change the text");
    const allText = sections.filter((s) => s.type === "text").map((s) => s.content || "").join("\n\n").trim();
    if (!allText) return toast.error("No text content to edit");
    setWholeEditBusy(true);
    try {
      const d = await api("POST", "ai/lesson-edit", {
        notes: allText, instructions: wholeEditInstr.trim(),
        module_title: courseTitle, lesson_title: title,
      });
      // Replace first text section, remove others
      let replaced = false;
      setSections((s) => s.filter((sec) => {
        if (sec.type !== "text") return true;
        if (!replaced) { replaced = true; return true; }
        return false;
      }).map((sec) => sec.type === "text" && !replaced ? sec : sec.type === "text" ? { ...sec, content: d.notes } : sec));
      // Actually simpler: just find first text section and update it, remove other text sections
      setSections((prev) => {
        const nonText = prev.filter((s) => s.type !== "text");
        const firstText = prev.find((s) => s.type === "text");
        const updated = firstText ? { ...firstText, content: d.notes } : { id: genId(), type: "text", content: d.notes, order: 0 };
        return [updated, ...nonText].map((s, i) => ({ ...s, order: i }));
      });
      setWholeEditInstr(""); setWholeEditMode(false);
      toast.success("AI edited all text — review before saving");
    } catch (e) { toast.error(e.message); }
    finally { setWholeEditBusy(false); }
  };

  const save = async () => {
    if (!title?.trim()) return toast.error("Give the lesson a title");
    setBusy(true);
    try {
      const orderedSections = sections.map((s, i) => ({ ...s, order: i }));
      const payload = {
        title, course_id: courseId, submodule_id: submoduleId,
        order: lesson ? lesson.order : nextOrder,
        sections: orderedSections,
      };
      const saved = lesson?.id
        ? await api("PUT", `lessons/${lesson.id}`, payload)
        : await api("POST", "lessons", payload);
      toast.success(lesson?.id ? "Lesson saved" : "Lesson added");
      onSaved(saved);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="rp-foam bg-white/70 p-6 md:p-7" data-testid="lesson-editor">
      <div className="flex items-center justify-between mb-5">
        <p className="rp-display text-xl font-medium text-[#262016]">{lesson?.id ? "Edit lesson" : "New lesson"}</p>
        <button onClick={onCancel} className="text-[#8A7E6B] hover:text-[#262016]" data-testid="lesson-editor-close"><X size={18} /></button>
      </div>

      <div className="space-y-4">
        <div>
          <label className={labelCls}>Lesson title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder="e.g. Understanding color harmony" className={inputCls} data-testid="lesson-field-title" autoFocus />
        </div>

        {/* Section list */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelCls}>Content sections</label>
            <div className="flex gap-1.5">
              {SECTION_TYPES.map(({ type, label, icon: Ic }) => (
                <button key={type} onClick={() => addSection(type)} data-testid={`section-add-${type}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium text-[#5C5042] border border-[#26201618] hover:border-[#B5793F] hover:text-[#B5793F] transition-colors">
                  <Ic size={10} /> {label}
                </button>
              ))}
            </div>
          </div>

          {sections.length === 0 && (
            <div className="rp-foam bg-white/45 p-6 text-center text-sm text-[#8A7E6B] italic">
              No sections yet — add text, video, image, or file above.
            </div>
          )}

          <div className="space-y-0">
            {sections.map((sec, idx) => {
              const Ic = SECTION_ICONS[sec.type] || Pencil;
              return (
                <div key={sec.id}>
                  <div className="rounded-xl border border-[#26201618] bg-white/80 p-4" data-testid={`section-${sec.id}`}
                       style={{ marginBottom: "8px" }}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="text-[#8A7E6B] hover:text-[#262016] disabled:opacity-25" data-testid={`section-up-${sec.id}`}><ChevronUp size={14} /></button>
                        <button onClick={() => moveSection(idx, 1)} disabled={idx === sections.length - 1} className="text-[#8A7E6B] hover:text-[#262016] disabled:opacity-25" data-testid={`section-down-${sec.id}`}><ChevronDown size={14} /></button>
                      </div>
                      <Ic size={13} className="text-[#B5793F]" />
                      <span className="text-[11px] uppercase tracking-wide font-semibold text-[#8A7E6B]">{SECTION_LABELS[sec.type]}</span>
                      <div className="flex-1" />
                      <button onClick={() => removeSection(idx)} data-testid={`section-delete-${sec.id}`}
                              className="text-[#8A7E6B] hover:text-red-600 transition-colors"><Trash2 size={13} /></button>
                    </div>
                    {sec.type === "text" && <TextSection section={sec} onChange={(u) => updateSection(idx, u)} courseTitle={courseTitle} lessonTitle={title} />}
                    {sec.type === "video" && <VideoSection section={sec} onChange={(u) => updateSection(idx, u)} lessonTitle={title} />}
                    {sec.type === "image" && <ImageSection section={sec} onChange={(u) => updateSection(idx, u)} courseTitle={courseTitle} lessonTitle={title} />}
                    {sec.type === "link" && <LinkSection section={sec} onChange={(u) => updateSection(idx, u)} />}
                    {sec.type === "file" && <FileSection section={sec} onChange={(u) => updateSection(idx, u)} />}
                  </div>
                  {idx < sections.length - 1 && <InsertButton onInsert={(type) => insertAt(idx, type)} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Whole-lesson AI controls */}
        <div className="border-t border-[#26201618] pt-4">
          <label className={labelCls}>Whole lesson AI</label>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={wholeAiWrite} disabled={wholeAiBusy || wholeEditBusy} data-testid="whole-ai-write"
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#B5793F] border border-[#B5793F55] rounded-full px-3 py-1 hover:bg-[#B5793F12] disabled:opacity-50">
              <Sparkles size={11} /> {wholeAiBusy ? "Writing…" : "Write all text with AI"}
            </button>
            <button onClick={() => setWholeEditMode(!wholeEditMode)} disabled={wholeAiBusy} data-testid="whole-ai-edit-toggle"
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#B5793F] border border-[#B5793F55] rounded-full px-3 py-1 hover:bg-[#B5793F12] disabled:opacity-50">
              <Pencil size={10} /> {wholeEditMode ? "Cancel" : "Edit all text with AI"}
            </button>
          </div>
          {wholeEditMode && (
            <div className="flex gap-2 mt-2">
              <input value={wholeEditInstr} onChange={(e) => setWholeEditInstr(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && wholeAiEdit()}
                     placeholder="e.g. Make everything more concise · Rewrite for beginners" className={inputCls} data-testid="whole-ai-edit-input" autoFocus />
              <button onClick={wholeAiEdit} disabled={wholeEditBusy} data-testid="whole-ai-edit-go"
                      className="shrink-0 inline-flex items-center gap-1 px-4 py-2 rounded-lg text-[12px] font-semibold text-[#F6F1E5] disabled:opacity-50" style={btnPrimary}>
                <Sparkles size={11} /> {wholeEditBusy ? "…" : "Apply"}
              </button>
            </div>
          )}
        </div>
      </div>

      <button onClick={save} disabled={busy} data-testid="lesson-editor-save"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-50" style={btnPrimary}>
        {busy ? "Saving…" : "Save lesson"}
      </button>
    </div>
  );
}

/* ---------- course builder: module details + sub-modules + lessons ---------- */
function CourseBuilder({ courseId, onBack }) {
  const [course, setCourse] = useState(null);
  const [submodules, setSubmodules] = useState([]);
  const [navPath, setNavPath] = useState([]); // stack of sub-module ids for drill-down
  const [lessons, setLessons] = useState([]);
  const [editingLesson, setEditingLesson] = useState(null);
  const [previewingLesson, setPreviewingLesson] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [newSubTitle, setNewSubTitle] = useState("");
  const [creatingSub, setCreatingSub] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [editSubTitle, setEditSubTitle] = useState("");
  const [bulkGenning, setBulkGenning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // {current, total, lessonTitle, result}
  const [bulkAuto, setBulkAuto] = useState(false);
  const [scopedFill, setScopedFill] = useState(null);
  const scopedPollRef = React.useRef(null);

  const currentSubId = navPath[navPath.length - 1] || null;

  const loadModule = useCallback(async () => {
    const mod = await api("GET", `modules/${courseId}`);
    setCourse(mod || null);
  }, [courseId]);

  const loadSubs = useCallback(async (parentId = null) => {
    const url = parentId
      ? `submodules?course_id=${courseId}&parent_id=${parentId}`
      : `submodules?course_id=${courseId}`;
    const subs = await api("GET", url);
    setSubmodules(subs);
  }, [courseId]);

  const loadLessons = useCallback(async (subId) => {
    const ls = await api("GET", `lessons?course_id=${courseId}&submodule_id=${subId}`);
    setLessons(ls);
  }, [courseId]);

  useEffect(() => {
    loadModule().catch(() => toast.error("Could not load module"));
    loadSubs().catch(() => {});
  }, [loadModule, loadSubs]);

  useEffect(() => {
    if (currentSubId) {
      loadSubs(currentSubId).catch(() => setSubmodules([]));
      loadLessons(currentSubId).catch(() => setLessons([]));
    } else {
      loadSubs(null).catch(() => setSubmodules([]));
      setLessons([]);
    }
  }, [currentSubId, loadSubs, loadLessons]);

  const setC = (k, v) => setCourse((c) => ({ ...c, [k]: v }));

  const saveDetails = async () => {
    if (!course.title?.trim()) return toast.error("Module needs a title");
    setSavingDetails(true);
    try {
      await api("PUT", `modules/${course.id}`, {
        title: course.title, tag: course.tag || "", description: course.description || "",
        cover: course.cover || "", published: course.published !== false, order: course.order || 0,
      });
      toast.success("Module saved");
      setDetailsOpen(false);
    } catch (e) { toast.error(e.message); }
    finally { setSavingDetails(false); }
  };

  const togglePublish = async () => {
    const next = course.published === false;
    setC("published", next);
    try {
      await api("PUT", `modules/${course.id}`, { published: next });
      toast.success(next ? "Module is now live" : "Module hidden");
    } catch { setC("published", !next); toast.error("Could not update"); }
  };

  const createSub = async () => {
    if (!newSubTitle.trim()) return toast.error("Enter a sub-module title");
    setCreatingSub(true);
    try {
      await api("POST", "submodules", { course_id: courseId, parent_id: currentSubId || null, title: newSubTitle.trim() });
      setNewSubTitle("");
      toast.success("Sub-module created");
      loadSubs(currentSubId);
    } catch (e) { toast.error(e.message); }
    finally { setCreatingSub(false); }
  };

  const saveSub = async (subId) => {
    if (!editSubTitle.trim()) return;
    try {
      await api("PUT", `submodules/${subId}`, { title: editSubTitle.trim() });
      toast.success("Renamed");
      setEditingSub(null);
      loadSubs(currentSubId);
    } catch (e) { toast.error(e.message); }
  };

  const removeSub = async (sub) => {
    if (!window.confirm(`Delete "${sub.title}" and all its contents?`)) return;
    try {
      await api("DELETE", `submodules/${sub.id}`);
      toast.success("Deleted");
      loadSubs(currentSubId);
    } catch { toast.error("Delete failed"); }
  };

  // Scoped fill for a sub-module
  const pollScopedFill = useCallback(async () => {
    try {
      const s = await api("GET", "scoped-fill/status");
      setScopedFill(s);
      if (!s.running && s.done) {
        if (scopedPollRef.current) { clearInterval(scopedPollRef.current); scopedPollRef.current = null; }
        if (currentSubId) loadLessons(currentSubId);
      }
    } catch { /* ignore */ }
  }, [currentSubId, loadLessons]);

  useEffect(() => {
    return () => { if (scopedPollRef.current) { clearInterval(scopedPollRef.current); scopedPollRef.current = null; } };
  }, []);

  const startSubFill = async (subId, subTitle) => {
    if (scopedFill?.running) return toast.info("A fill is already running");
    if (!window.confirm(`Fill missing content for "${subTitle}"?\n\nAdds text, videos, and links only where missing.`)) return;
    try {
      await api("POST", "scoped-fill/start", { course_id: courseId, submodule_id: subId });
      toast.success(`Filling "${subTitle}" in background…`);
      setScopedFill({ running: true, total: 0, processed: 0, scope: subTitle, current: "Scanning..." });
      scopedPollRef.current = setInterval(pollScopedFill, 4000);
    } catch (e) { toast.error(e.message); }
  };

  const stopSubFill = async () => {
    try {
      await api("POST", "scoped-fill/stop");
      toast.info("Stopping fill…");
    } catch (e) { toast.error(e.message); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= lessons.length) return;
    const reordered = [...lessons];
    [reordered[idx], reordered[j]] = [reordered[j], reordered[idx]];
    setLessons(reordered);
    try {
      await Promise.all(reordered.map((l, i) => (l.order !== i ? api("PUT", `lessons/${l.id}`, { order: i }) : null)));
      setLessons(reordered.map((l, i) => ({ ...l, order: i })));
    } catch { toast.error("Reorder failed"); loadLessons(currentSubId); }
  };

  const removeLesson = async (l) => {
    if (!window.confirm(`Delete lesson "${l.title}"?`)) return;
    try {
      await api("DELETE", `lessons/${l.id}`);
      toast.success("Lesson deleted");
      setEditingLesson(null);
      loadLessons(currentSubId);
    } catch { toast.error("Delete failed"); }
  };

  const bulkGenerate = async (auto = false) => {
    const empty = lessons.filter((l) => {
      const hasContent = (l.notes || "").trim() || (l.sections && l.sections.length > 0 && l.sections.some(s => (s.content || s.url || "").trim()));
      return !hasContent;
    });
    if (empty.length === 0) { toast.info("All lessons already have content"); return; }
    const subTitle = submodules.find(s => s.id === currentSubId)?.title || "";
    setBulkGenning(true);
    setBulkAuto(auto);

    for (let i = 0; i < empty.length; i++) {
      const l = empty[i];
      setBulkProgress({ current: i + 1, total: empty.length, lessonTitle: l.title, result: null, waiting: false });
      try {
        const d = await api("POST", "ai/lesson-content", {
          lesson_id: l.id, module_title: course.title,
          submodule_title: subTitle, lesson_title: l.title,
          search_youtube: true, generate_sections: true,
        });
        const sectionCount = (d.lesson?.sections || []).length;
        const result = { text: d.generated_text, video: d.found_video, links: d.found_links || 0, sections: sectionCount };
        setBulkProgress({ current: i + 1, total: empty.length, lessonTitle: l.title, result, waiting: !auto && i < empty.length - 1 });

        if (!auto && i < empty.length - 1) {
          // Wait for user to click "Next"
          await new Promise((resolve) => {
            window.__bulkNext = resolve;
          });
        }
      } catch (e) {
        console.error("Fill failed for", l.title, e);
        setBulkProgress((p) => ({ ...p, result: { error: true }, waiting: !auto && i < empty.length - 1 }));
        if (!auto && i < empty.length - 1) {
          await new Promise((resolve) => { window.__bulkNext = resolve; });
        }
      }
    }
    setBulkGenning(false);
    setBulkProgress(null);
    setBulkAuto(false);
    toast.success("All lessons filled");
    loadLessons(currentSubId);
  };

  const bulkNext = () => {
    if (window.__bulkNext) { window.__bulkNext(); window.__bulkNext = null; }
  };

  const bulkStop = () => {
    setBulkAuto(false);
    setBulkGenning(false);
    setBulkProgress(null);
    if (window.__bulkNext) { window.__bulkNext(); window.__bulkNext = null; }
    loadLessons(currentSubId);
  };

  if (!course)
    return <p className="rp-display rp-italic text-[#5C5042]">One moment…</p>;

  const currentSub = null; // deprecated, use navPath

  const goBack = () => {
    if (navPath.length > 0) {
      setNavPath((p) => p.slice(0, -1));
      setEditingLesson(null);
    } else {
      onBack();
    }
  };

  const openSubModule = (subId) => {
    setNavPath((p) => [...p, subId]);
    setEditingLesson(null);
  };

  // Does the current level have child sub-modules or only lessons?
  const hasChildren = submodules.length > 0 && currentSubId;
  const showSubList = !currentSubId || submodules.some((s) => s.child_count > 0 || !currentSubId);

  return (
    <div data-testid="course-builder">
      <button onClick={goBack} data-testid="builder-back-btn"
              className="inline-flex items-center gap-2 mb-5 text-sm font-medium text-[#5C5042] hover:text-[#262016]">
        <ArrowLeft size={15} /> {navPath.length > 0 ? "Back" : "All modules"}
      </button>

      {/* module header */}
      <div className="rp-foam bg-white/55 p-5 md:p-6 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          {course.cover && <img src={resolve(course.cover)} alt="" className="w-14 h-14 rounded-lg object-cover border border-[#26201620]" />}
          <div className="flex-1 min-w-[180px]">
            <p className="rp-display text-2xl font-light text-[#262016]" data-testid="builder-course-title">{course.title}</p>
            <p className="text-xs text-[#8A7E6B]">{submodules.length} sub-module{submodules.length === 1 ? "" : "s"} {course.tag ? `· ${course.tag}` : ""}</p>
          </div>
          <button onClick={togglePublish} data-testid="builder-publish-toggle"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold border transition-colors"
                  style={course.published !== false
                    ? { background: "#B5793F", color: "#F6F1E5", borderColor: "#B5793F" }
                    : { borderColor: "rgba(38,32,22,0.25)", color: "#5C5042" }}>
            {course.published !== false ? <><Eye size={13} /> Live</> : <><EyeOff size={13} /> Draft</>}
          </button>
          <button onClick={() => setDetailsOpen((o) => !o)} data-testid="builder-edit-details"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-medium border border-[#26201625] text-[#262016] hover:border-[#B5793F]">
            <Pencil size={13} /> Details
          </button>
        </div>

        {detailsOpen && (
          <div className="mt-5 pt-5 border-t border-[#26201618] space-y-4" data-testid="builder-details-form">
            <div><label className={labelCls}>Title</label>
              <input value={course.title || ""} onChange={(e) => setC("title", e.target.value)} className={inputCls} data-testid="course-field-title" /></div>
            <div><label className={labelCls}>Tag</label>
              <input value={course.tag || ""} onChange={(e) => setC("tag", e.target.value)} className={inputCls} data-testid="course-field-tag" /></div>
            <div><label className={labelCls}>Description</label>
              <textarea value={course.description || ""} onChange={(e) => setC("description", e.target.value)} rows={2} className={inputCls} data-testid="course-field-description" /></div>
            <div><label className={labelCls}>Cover image</label>
              <div className="flex gap-2 items-center">
                {course.cover && <img src={resolve(course.cover)} alt="" className="w-12 h-12 rounded-lg object-cover border border-[#26201620]" />}
                <UploadBtn accept="image/*" testid="course-upload-cover" label={course.cover ? "Replace" : "Upload cover"}
                           onDone={(href) => setC("cover", href)} />
                {course.cover && <button onClick={() => setC("cover", "")} className="text-xs text-[#8A7E6B] underline">remove</button>}
              </div></div>
            <button onClick={saveDetails} disabled={savingDetails} data-testid="course-details-save"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-50" style={btnPrimary}>
              {savingDetails ? "Saving…" : "Save details"}
            </button>
          </div>
        )}
      </div>

      {/* sub-module list + lesson editor */}
      <div>
        {/* Add sub-module/sub-section */}
        <div className="rp-foam bg-white/70 p-5 mb-4">
          <label className={labelCls}>{currentSubId ? "Add sub-section" : "Add sub-module"}</label>
          <div className="flex gap-2">
            <input value={newSubTitle} onChange={(e) => setNewSubTitle(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && createSub()}
                   placeholder={currentSubId ? "Sub-section title — e.g. Photoshop" : "Sub-module title — e.g. Prompting Fundamentals"} className={inputCls} data-testid="sub-new-title" />
            <button onClick={createSub} disabled={creatingSub} data-testid="sub-add-btn"
                    className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-50" style={btnPrimary}>
              <Plus size={14} /> {creatingSub ? "Adding…" : "Add"}
            </button>
          </div>
        </div>

        {/* Sub-module list */}
        {submodules.length > 0 && (
          <div className="mb-5" data-testid="sub-list">
            {/* Scoped fill progress */}
            {scopedFill?.running && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-4" data-testid="builder-scoped-fill-progress">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-900">Filling: {scopedFill.scope}</p>
                    <p className="text-[12px] text-blue-600 mt-1 flex items-center gap-2">
                      <RefreshCw size={12} className="animate-spin" />
                      {scopedFill.current || `${scopedFill.processed || 0}/${scopedFill.total || "?"}`}
                    </p>
                  </div>
                  <button onClick={stopSubFill} data-testid="builder-scoped-fill-stop"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-red-400 text-red-600 hover:bg-red-50">
                    <X size={11} /> Stop
                  </button>
                </div>
              </div>
            )}
            {scopedFill?.done && !scopedFill?.running && scopedFill.total > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-3 mb-4 text-[13px] text-green-700 flex items-center gap-2" data-testid="builder-scoped-fill-done">
                <Check size={13} />
                {scopedFill.scope}: {scopedFill.text_done || 0} text, {scopedFill.video_done || 0} videos, {scopedFill.link_done || 0} links{scopedFill.errors > 0 ? ` · ${scopedFill.errors} errors` : ""}
              </div>
            )}
            {submodules.map((s) => (
              <div key={s.id} className="bg-white rounded-2xl flex items-center gap-4 px-5 py-4 border border-[#d4cbb8] shadow-sm" style={{ marginBottom: "20px" }} data-testid={`sub-item-${s.id}`}>
                {editingSub === s.id ? (
                  <div className="flex-1 flex gap-2">
                    <input value={editSubTitle} onChange={(e) => setEditSubTitle(e.target.value)}
                           onKeyDown={(e) => e.key === "Enter" && saveSub(s.id)}
                           className={inputCls} autoFocus data-testid="sub-rename-input" />
                    <button onClick={() => saveSub(s.id)} className="text-xs font-medium text-[#B5793F]">Save</button>
                    <button onClick={() => setEditingSub(null)} className="text-xs text-[#8A7E6B]">Cancel</button>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openSubModule(s.id)} data-testid={`sub-open-${s.id}`}>
                      <p className="rp-display text-base md:text-lg font-medium text-[#262016] truncate">{s.title}</p>
                      <p className="text-xs text-[#8A7E6B]">
                        {s.child_count > 0 ? `${s.child_count} section${s.child_count === 1 ? "" : "s"} · ` : ""}
                        {s.lesson_count || 0} lesson{s.lesson_count === 1 ? "" : "s"}
                      </p>
                    </div>
                    <button onClick={() => openSubModule(s.id)} data-testid={`sub-open-btn-${s.id}`}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold text-[#F6F1E5]" style={btnPrimary}>
                      Open
                    </button>
                    <button onClick={() => startSubFill(s.id, s.title)} data-testid={`sub-fill-${s.id}`}
                            disabled={scopedFill?.running}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-semibold border border-[#B5793F] text-[#B5793F] hover:bg-[#B5793F]/10 disabled:opacity-40"
                            title="Fill missing content for this section">
                      <Sparkles size={12} /> Fill
                    </button>
                    <button onClick={() => { setEditingSub(s.id); setEditSubTitle(s.title); }} title="Rename" data-testid={`sub-edit-${s.id}`}
                            className={iconBtn}><Pencil size={13} /></button>
                    <button onClick={() => removeSub(s)} title="Delete" data-testid={`sub-delete-${s.id}`}
                            className={`${iconBtn} hover:text-red-700 hover:border-red-700`}><Trash2 size={14} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Lessons at this level */}
        {currentSubId && (
          <>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A7E6B] font-medium">Lessons</p>
              <div className="flex items-center gap-2">
                <button onClick={() => bulkGenerate(false)} disabled={bulkGenning} data-testid="bulk-gen-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-semibold border border-[#B5793F55] text-[#B5793F] hover:bg-[#B5793F12] disabled:opacity-50">
                  <Sparkles size={12} /> {bulkGenning ? "Filling…" : "Fill lessons with AI (step by step)"}
                </button>
                <button onClick={() => bulkGenerate(true)} disabled={bulkGenning} data-testid="bulk-gen-auto-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-semibold border border-[#26201625] text-[#5C5042] hover:bg-[#26201608] disabled:opacity-50">
                  <RefreshCw size={12} /> Auto-fill all
                </button>
              </div>
            </div>
            {bulkProgress && (
              <div className="rp-foam bg-white/80 p-4 mb-4 border border-[#B5793F33]" data-testid="bulk-gen-progress">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[13px] font-semibold text-[#262016]">
                    Filling lesson {bulkProgress.current}/{bulkProgress.total}
                  </span>
                  <button onClick={bulkStop} className="text-[11px] text-[#8A7E6B] hover:text-red-600" data-testid="bulk-gen-stop">
                    Stop
                  </button>
                </div>
                <p className="text-sm text-[#5C5042] mb-2 truncate">{bulkProgress.lessonTitle}</p>
                <div className="w-full h-1.5 rounded-full bg-[#26201610] mb-2">
                  <div className="h-full rounded-full bg-[#B5793F] transition-all" style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }} />
                </div>
                {bulkProgress.result && !bulkProgress.result.error && (
                  <div className="flex items-center gap-3 text-[12px] text-[#5C5042] mb-2">
                    <span className="flex items-center gap-1"><Check size={11} className="text-[#B5793F]" /> {bulkProgress.result.sections} sections</span>
                    {bulkProgress.result.video && <span className="flex items-center gap-1"><PlayCircle size={11} /> video found</span>}
                    {bulkProgress.result.links > 0 && <span className="flex items-center gap-1"><Link2 size={11} /> {bulkProgress.result.links} links</span>}
                  </div>
                )}
                {bulkProgress.result?.error && (
                  <p className="text-[12px] text-red-600 mb-2">Failed — will try next lesson</p>
                )}
                {bulkProgress.waiting && (
                  <div className="flex items-center gap-2">
                    <button onClick={bulkNext} data-testid="bulk-gen-next"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-semibold text-[#F6F1E5]" style={btnPrimary}>
                      Fill next lesson
                    </button>
                    <button onClick={bulkStop} className="text-[12px] text-[#8A7E6B] hover:text-[#262016]">Done for now</button>
                  </div>
                )}
                {!bulkProgress.waiting && bulkGenning && (
                  <div className="flex items-center gap-2 text-[12px] text-[#B5793F]">
                    <RefreshCw size={11} className="animate-spin" /> Generating content…
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
              <aside className="rp-foam bg-white/45 p-4 h-fit" data-testid="builder-lesson-list">
                <div className="space-y-1.5">
                  {lessons.map((l, i) => (
                    <div key={l.id}
                         className="flex items-center gap-1.5 rounded-lg px-2 py-2 transition-colors"
                         style={{ background: (editingLesson?.id === l.id || previewingLesson?.id === l.id) ? "rgba(38,32,22,0.08)" : "transparent" }}
                         data-testid={`builder-lesson-${l.id}`}>
                      <div className="flex flex-col">
                        <button onClick={() => move(i, -1)} disabled={i === 0} className="text-[#8A7E6B] hover:text-[#262016] disabled:opacity-25" aria-label="Move up"><ChevronUp size={14} /></button>
                        <button onClick={() => move(i, 1)} disabled={i === lessons.length - 1} className="text-[#8A7E6B] hover:text-[#262016] disabled:opacity-25" aria-label="Move down"><ChevronDown size={14} /></button>
                      </div>
                      <button onClick={() => { setPreviewingLesson(l); setEditingLesson(null); }} className="flex-1 min-w-0 text-left" data-testid={`lesson-open-${l.id}`}>
                        <span className="block text-sm font-medium text-[#262016] truncate">{i + 1}. {l.title}</span>
                        <span className="block text-[11px] text-[#8A7E6B] truncate flex items-center gap-1">
                          {(l.sections?.length > 0 || (l.notes || "").trim()) ? <><Check size={10} className="text-[#B5793F]" /> content</> : "empty"}
                          {" · "}{(l.videos || []).length || l.video ? <><PlayCircle size={10} /> vid</> : ""}
                          {l.sections?.some(s => s.type === "link") ? <> · <Link2 size={10} /> links</> : ""}
                          {l.sections?.some(s => s.type === "video") ? <> · <PlayCircle size={10} /> vid</> : ""}
                        </span>
                      </button>
                      <button onClick={() => removeLesson(l)} className="text-[#8A7E6B] hover:text-red-700" aria-label="Delete lesson"><Trash2 size={13} /></button>
                    </div>
                  ))}
                  {lessons.length === 0 && submodules.length === 0 && <p className="px-2 py-3 text-xs text-[#8A7E6B] italic">No lessons yet — add the first one.</p>}
                </div>
                <button onClick={() => { setEditingLesson({}); setPreviewingLesson(null); }} data-testid="builder-add-lesson"
                        className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5]" style={btnPrimary}>
                  <Plus size={14} /> Add lesson
                </button>
              </aside>

              <div>
                {editingLesson !== null ? (
                  <LessonEditor courseId={course.id} courseTitle={course.title}
                                submoduleId={currentSubId}
                                lesson={editingLesson.id ? editingLesson : null}
                                nextOrder={lessons.length}
                                onSaved={() => { setEditingLesson(null); setPreviewingLesson(null); loadLessons(currentSubId); }}
                                onCancel={() => { setEditingLesson(null); }} />
                ) : previewingLesson !== null ? (
                  <LessonPreview
                    lesson={previewingLesson}
                    onEdit={() => { setEditingLesson(previewingLesson); setPreviewingLesson(null); }}
                    onClose={() => setPreviewingLesson(null)} />
                ) : (
                  <div className="rp-foam bg-white/45 p-10 text-center" data-testid="builder-empty-editor">
                    <BookOpen className="mx-auto mb-3 text-[#B5793F]" size={22} />
                    <p className="rp-display rp-italic text-[#8A7E6B]">
                      {lessons.length ? "Select a lesson to preview, or add a new one." : "Add your first lesson."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {!currentSubId && submodules.length === 0 && (
          <p className="rp-foam bg-white/55 p-6 text-sm text-[#8A7E6B]">No sub-modules yet — add your first one above.</p>
        )}
      </div>
    </div>
  );
}



export { CourseBuilder };
